"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { TrashIcon } from "../_icons/trashIcon";
import { AgeRestricted } from "../ageRestricted/ageRestricted";
import { PrimaryButton } from "../button/primaryButton";
import { CartMerchantInfo } from "../cartDrawer/cartMerchantInfo";
import {
  SuccessBanner,
  UnavailableBanner,
} from "../cartDrawer/cartStatusBanner";
import { DeliveryEstimateBanner } from "../deliveryEstimateBanner/deliveryEstimateBanner";
import { OrderTypeDropdown } from "../orderTypeDropdown/orderTypeDropdown";
import { ScheduledOrderDropdown } from "../scheduledOrderDropdown/scheduledOrderDropdown";
import { useRouter } from "next13-progressbar";
import { CartDrawerLayout } from "../cartDrawer/cartDrawerLayout";
import { GiftCardCartItem } from "@/app/(shop)/giftcard/components/giftCardCartItem";
import { GiftCardCartOrdersBanner } from "@/app/(shop)/giftcard/components/giftCardCartOrdersBanner";
import { InactiveMenuCartOrdersBanner } from "@/app/(shop)/giftcard/components/InactiveMenuCartOrdersBanner";
import { useCurrency } from "@/app/providers/currencyContext";
import ArrowRightIcon from "@/components/_icons/arrowRightIcon";
import { OrderType } from "@/constants/enum";
import { DayPart } from "@/dto/dayPartMenu";
import { ScheduledTime } from "@/dto/scheduledTime";
import { Cart } from "@/models/cart/cart";
import { RestaurantCart } from "@/models/cart/restaurantCart";
import { RestaurantCartItem } from "@/models/cart/types";
import { HHMMtoDate } from "@/utils/dateTimeUtils";
import { getMenuDisplayName } from "@/utils/dayPartMenuUtils";
import { isBusinessOpen } from "@/utils/isBusinessOpenUtil";
import { getItemUrl } from "@/utils/itemUrlUtils";
import { removeFromLocalStorage } from "@/utils/localStorageUtils";
import { modifierDisplay } from "@/utils/modifierDisplayUtil";
import pom from "@/utils/pom";
import { canMerchantAcceptOrders } from "@/utils/scheduleOrderUtils";
import { getFromSessionStorage } from "@/utils/sessionStorageUtils";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { applyDataTestId } from "@/utils/testUtils";
import useMediaQuery from "@/utils/useMediaQuery";
import { isEmpty } from "lodash";
import { useTranslations } from "next-intl";
import { usePathname, useSearchParams } from "next/navigation";
import { Merchant } from "@/dto/merchant";
import { GiftCard } from "@/dto/giftCard";
import { checkIsHomePage } from "@/utils/nativeBridgeUtils";
import {
  ReorderUnavailableItem,
  readAndClearReorderSession,
} from "@/utils/reorderValidation";

interface RestaurantCartItemViewProps {
  isOpen?: boolean;
  onClose: () => void;
  onRequestClose: () => void;
  merchant: Merchant;
  hash: string;
  cartItems: RestaurantCartItem[];
  orderType: OrderType;
  setOrderType: React.Dispatch<React.SetStateAction<OrderType>>;
  scheduledOrder: any;
  setScheduledOrder: any;
  setShowScheduledOrderForm: React.Dispatch<React.SetStateAction<boolean>>;
  // eslint-disable-next-line no-unused-vars
  removeFromCart: (event: React.MouseEvent, index: any) => void;
  checkoutDisabled: boolean;
  merchantColor: string;
  handleContinueToCheckout: () => void;
  // eslint-disable-next-line no-unused-vars
  setIsRestaurantCartOpen: (value?: boolean) => void;
  giftCard: GiftCard | null;
  setGiftCard: React.Dispatch<React.SetStateAction<GiftCard | null>>;
  selectedDayPart?: DayPart | null;
}

export const RestaurantCartItemView = ({
  isOpen,
  onClose,
  onRequestClose,
  merchant,
  hash,
  cartItems,
  orderType,
  setOrderType,
  scheduledOrder,
  setScheduledOrder,
  setShowScheduledOrderForm,
  removeFromCart,
  checkoutDisabled,
  merchantColor,
  handleContinueToCheckout,
  setIsRestaurantCartOpen,
  giftCard,
  setGiftCard,
  selectedDayPart = null,
}: RestaurantCartItemViewProps) => {
  const tPickup = useTranslations("CalculatePickTime");
  const router = useRouter();

  const searchParams = useSearchParams();
  const pathName = usePathname();
  const menuName = searchParams?.get("menu");
  const storedSchedule = getFromSessionStorage<Partial<ScheduledTime>>(
    SessionStorageKey.SCHEDULED_TIME,
    {}
  );
  const isHomePage = checkIsHomePage(
    pathName,
    merchant.merchantMeta.selectedSetup === "Simple",
    merchant.isStandaloneGiftCardWebsite
  );

  const [unavailableItemsCriteria, setUnavailableItemsCriteria] = useState<
    "past" | "inactive" | null
  >(null);

  const [reorderUnavailable, setReorderUnavailable] = useState<{
    needsReAdd: ReorderUnavailableItem[];
    hardUnavailable: ReorderUnavailableItem[];
  } | null>(null);
  const [reorderBannerDismissed, setReorderBannerDismissed] = useState(false);
  const [reorderAddedCount, setReorderAddedCount] = useState<number | null>(
    null
  );

  useEffect(() => {
    const isReorderHash =
      hash === "#restaurant-cart" || hash === "#restaurant-cart-unavailable";
    if (!isReorderHash) return;

    const isUnavailableHash = hash === "#restaurant-cart-unavailable";
    const { unavailableItems, addedCount } =
      readAndClearReorderSession(isUnavailableHash);

    if (isUnavailableHash) {
      setReorderBannerDismissed(false);
      if (unavailableItems) setReorderUnavailable(unavailableItems);
    }
    if (addedCount !== null) setReorderAddedCount(addedCount);
  }, [hash]);

  const businessOpen = useMemo(() => isBusinessOpen(merchant), [merchant]);
  const hasItemsInCart = cartItems.length > 0;
  const handleItemClick = (item: RestaurantCartItem) => {
    const selectedItem = item.value;
    const queryParams = new URLSearchParams();
    if (menuName) queryParams.set("menu", menuName || "");
    if (merchant.isRestaurant) {
      queryParams.set("cartId", selectedItem.cartId || "");
    }
    router.push(`/menu/${getItemUrl(selectedItem)}/?${queryParams.toString()}`);
    onClose();
  };

  const { format } = useCurrency();
  const t = useTranslations("RestaurantCartItemView");
  const isMobileScreen = useMediaQuery("(max-width: 640px)");

  useEffect(() => {
    if (isEmpty(cartItems) || !merchant?.isRestaurant) return;

    /**
     * When cart has items and hash has value that means the items are added from order again scenario
     * In this case check if merchant accepting orders (ASAP or scheduling)
     * If current time is outside business hours or in menu gap and merchant cannot accept order scheduling
     * based on business hours/chosen menu times then show a message and disable checkout
     */
    const merchantAcceptingOrders = canMerchantAcceptOrders(
      merchant,
      false,
      selectedDayPart,
      tPickup
    );

    if (!merchantAcceptingOrders && selectedDayPart != null)
      setUnavailableItemsCriteria("inactive");
  }, [cartItems, merchant, selectedDayPart]);

  const handleCheckoutClick = () => {
    if (!isEmpty(selectedDayPart)) {
      const cartItemsStatus = RestaurantCart.getRestaurantCartItemsStatus(
        cartItems,
        storedSchedule.asap ?? false,
        selectedDayPart,
        merchant.timezone
      );

      if (cartItemsStatus !== "active") {
        setUnavailableItemsCriteria(cartItemsStatus);
        return;
      }
    }

    handleContinueToCheckout();
  };

  const getInactiveItemsMessage = (
    dayPart: DayPart | null,
    criteria: "past" | "inactive",
    timezone: string
  ): string => {
    switch (criteria) {
      case "inactive":
        return t("invalidItems", {
          itemsText: giftCard ? t("nonGiftCardItems") : t("items"),
        });
      case "past":
        if (!isEmpty(dayPart)) {
          const currentMenuName = getMenuDisplayName(dayPart?.menuName, t);
          const time = HHMMtoDate(
            dayPart?.time?.endTime.toString().padStart(4, "0"),
            timezone,
            new Date()
          ).toLocaleString({
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });

          return `${t("dayPartMenuMessage", {
            menuName: currentMenuName,
            time,
          })} ${giftCard ? t("giftCardMessage") : ""} ${t("switchMenu")}`;
        }
        return "";
      default:
        return "";
    }
  };

  const totalCount =
    cartItems.reduce((acc, item) => acc + (item.value?.quantity || 0), 0) +
    (giftCard ? 1 : 0);

  const onlyGiftCardInCart = !hasItemsInCart && giftCard;
  const originalItemsCountRef = useRef(totalCount);

  useEffect(() => {
    if (originalItemsCountRef.current === 0 && totalCount > 0) {
      originalItemsCountRef.current = totalCount;
    }
  }, [totalCount]);

  const title = t("yourCart", {
    count: totalCount,
  });

  const closeHandler = () => {
    Cart.removeHashFromURL(hash, pathName, searchParams);
    onClose();
  };

  const getBannerMessage = () => {
    if (isEmpty(selectedDayPart)) {
      return "";
    }

    const menuName = getMenuDisplayName(selectedDayPart?.menuName, t);

    if (cartItems.length > 0) {
      return t("partialItemsAddedToCart", {
        count: cartItems.length,
        menuName,
      });
    }

    return t("allItemsUnavailable", { menuName });
  };

  return (
    <CartDrawerLayout
      isOpen={isOpen ?? false}
      isHomePage={isHomePage}
      onClose={closeHandler}
      onRequestClose={onRequestClose}
      className="md:w-[640px]"
      title={title}
      testId={applyDataTestId(pom.restaurantCart.cartDrawer)}
      closeCartLabel={t("closeCart")}
      hasItems={hasItemsInCart || !!onlyGiftCardInCart}
      headerContent={
        <>
          {isMobileScreen && <CartMerchantInfo merchant={merchant} />}
          <div className="px-4 pt-[25px] sm:pb-1 grid md:grid-cols-2 items-stretch gap-4 sm:pt-8">
            <OrderTypeDropdown
              orderType={orderType}
              setOrderType={setOrderType}
              availableOrderTypes={merchant.services}
            />
            <ScheduledOrderDropdown
              scheduledOrder={scheduledOrder}
              setScheduledOrder={setScheduledOrder}
              showScheduleScreen={() => setShowScheduledOrderForm(true)}
              leadTime={merchant?.leadTime}
              merchant={merchant}
              orderType={orderType}
              selectedDayPart={selectedDayPart}
            />
          </div>
          {((hash === "#restaurant-cart" && totalCount > 0) ||
            (hash === "#restaurant-cart-unavailable" &&
              !reorderBannerDismissed)) && (
            <div className="pt-4">
              {hash === "#restaurant-cart" && totalCount > 0 && (
                <SuccessBanner
                  count={
                    reorderAddedCount ??
                    (originalItemsCountRef.current || totalCount)
                  }
                />
              )}
              {hash === "#restaurant-cart-unavailable" &&
                !reorderBannerDismissed && (
                  <UnavailableBanner
                    count={
                      reorderAddedCount ??
                      (originalItemsCountRef.current || totalCount)
                    }
                    message={getBannerMessage()}
                    needsReAdd={reorderUnavailable?.needsReAdd}
                    hardUnavailable={reorderUnavailable?.hardUnavailable}
                    dayPartName={
                      selectedDayPart &&
                      reorderUnavailable &&
                      (reorderUnavailable.needsReAdd.length > 0 ||
                        reorderUnavailable.hardUnavailable.length > 0)
                        ? getMenuDisplayName(selectedDayPart.menuName, t)
                        : undefined
                    }
                    onDismiss={() => setReorderBannerDismissed(true)}
                  />
                )}
            </div>
          )}
        </>
      }
      footerContent={
        <PrimaryButton
          style={{
            backgroundColor:
              (checkoutDisabled ||
                unavailableItemsCriteria === "inactive" ||
                unavailableItemsCriteria === "past") &&
              !onlyGiftCardInCart
                ? "#C7C7C7"
                : merchantColor,
          }}
          className="bg-black text-white font-bold py-2 rounded-none sm:rounded-md w-full h-16 sm:h-14 disabled:bg-[#C7C7C7] flex justify-between sm:justify-center items-center px-[18px] tracking-wide"
          onClick={handleCheckoutClick}
          disabled={
            (checkoutDisabled ||
              unavailableItemsCriteria === "inactive" ||
              unavailableItemsCriteria === "past") &&
            !onlyGiftCardInCart
          }
          aria-label={t("continueToCheckout")}
        >
          <p className="text-xl font-medium">{t("continueToCheckout")}</p>
          {isMobileScreen && (
            <ArrowRightIcon
              className="sm:hidden"
              color="white"
              width={24}
              height={24.7}
            />
          )}
        </PrimaryButton>
      }
      ariaLabel={t("yourCartLabel")}
    >
      {storedSchedule.orderType === OrderType.DELIVERY && hasItemsInCart && (
        <DeliveryEstimateBanner
          merchant={merchant}
          onAddMore={setIsRestaurantCartOpen}
        />
      )}
      {!businessOpen && hasItemsInCart && giftCard && (
        <GiftCardCartOrdersBanner />
      )}
      {cartItems.length > 0 &&
      (unavailableItemsCriteria === "inactive" ||
        unavailableItemsCriteria === "past") ? (
        <InactiveMenuCartOrdersBanner
          message={getInactiveItemsMessage(
            selectedDayPart,
            unavailableItemsCriteria,
            merchant.timezone
          )}
        />
      ) : null}
      {cartItems.map((item: any, index: any) => (
        <div
          key={`${item.key}-${index}`}
          className="flex flex-row justify-between items-center py-4 border-b cursor-pointer"
          data-testid={applyDataTestId(pom.restaurantCart.items)}
        >
          <button
            onClick={() => handleItemClick(item)}
            className="w-full flex flex-col justify-between items-start"
            data-testid={applyDataTestId(pom.restaurantCart.itemName)}
            aria-label={t("editItem", { itemName: item.value?.name })}
          >
            <p className="text-lg text-[#373737] max-w-[310px] text-left">
              {item.value?.name}{" "}
              {item.value?.quantity > 1 &&
                t("quantityMultiplier", { count: item.value?.quantity })}
            </p>
            {item.value?.isAgeRestricted && <AgeRestricted />}
            <div className="max-w-[80%]">
              {item.value?.modifiers?.length > 0 && (
                <p className="text-[15px] text-[#727272] text-left">
                  {modifierDisplay(item.value?.modifiers)}
                </p>
              )}
              <div className="w-full">
                <p className="text-[15px] text-[#727272] max-w-[310px] break-words text-left">
                  {item.value?.specialInstructions}
                </p>
              </div>
            </div>
          </button>
          <div className="flex flex-col justify-start items-end">
            <p
              className="text-[16px] font-medium mt-0.5"
              data-testid={applyDataTestId(pom.restaurantCart.itemPrice)}
            >
              {format(item.value?.total)}
            </p>
            <button
              onClick={(event: React.MouseEvent) => {
                removeFromCart(event, index);
                setReorderBannerDismissed(true);
              }}
              className="mt-2"
              aria-label={t("removeItem", { itemName: item.value?.name })}
            >
              <TrashIcon width={14} height={14} />
            </button>
          </div>
        </div>
      ))}
      {giftCard && (
        <GiftCardCartItem
          giftCard={giftCard}
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
            removeFromLocalStorage(LocalStorageKey.GIFT_CARD);
            setGiftCard(null);
          }}
          onRequestClose={onClose}
          showBottomBorder={true}
        />
      )}
    </CartDrawerLayout>
  );
};
